import { useState, useEffect } from 'react';
import axios from 'axios';

const EU_COUNTRIES = [
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE',
  'GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT',
  'RO','SK','SI','ES','SE'
];

const CACHE_KEY = 'geo:isEU';
// 200 hours in ms
const CACHE_TTL = 200 * 60 * 60 * 1000;

export type Geo = { country: string | null; currency: string | null };

const NO_GEO: Geo = { country: null, currency: null };

const readCache = (): Geo | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { timestamp, country, currency } = JSON.parse(raw);
    // Entries from the older { value, timestamp } shape lack `country`; refetch once.
    if (!country || Date.now() - timestamp >= CACHE_TTL) return null;
    return { country, currency: currency ?? null };
  } catch {
    return null;
  }
};

const writeCache = (geo: Geo) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...geo, timestamp: Date.now() }));
  } catch {
    // storage unavailable
  }
};

const fetchGeo = async (): Promise<Geo> => {
  try {
    const key = "d5a756a764e248854368e06ff31e506948471e7c40bffa73c0dde8c8";
    const resp = await axios.get(`https://api.ipdata.co?api-key=${key}&fields=country_code,currency`);
    const country = (resp.data?.country_code as string | undefined) || null;
    if (!country) return NO_GEO;
    const geo = { country, currency: (resp.data?.currency?.code as string | undefined) || null };
    writeCache(geo);
    return geo;
  } catch {
    return NO_GEO;
  }
};

// One ipdata lookup per browser per CACHE_TTL, shared by every caller
// (consent banner + pricing page) via the localStorage cache and a
// module-scoped in-flight promise.
let inflight: Promise<Geo> | null = null;

export function getGeo(): Promise<Geo> {
  const cached = readCache();
  if (cached) return Promise.resolve(cached);
  if (!inflight) inflight = fetchGeo().finally(() => { inflight = null; });
  return inflight;
}

export function useIsEU() {
  const [isEU, setIsEU] = useState<boolean | null>(null);

  useEffect(() => {
    getGeo().then(({ country }) => setIsEU(country != null && EU_COUNTRIES.includes(country)));
  }, []);

  return isEU;
}
